import { useCallback, useEffect, useMemo, useState } from 'react';
import { deletePnlReport, listAllPnlReports, setPnlReportActivation } from 'src/components/star-grid/services/starGridService';
import { normalizeReportStatus } from 'src/components/star-grid/utils/reportStatus';
import type { PnlReportSummary } from 'src/components/star-grid/starGridTypes';

/** The lifecycle a row is in. Deleted wins: a deleted report is not merely deactivated. */
export type ManagedReportState = 'active' | 'deactivated' | 'deleted';

export const managedReportState = (report: PnlReportSummary): ManagedReportState =>
  normalizeReportStatus(report.status) === 'DELETED' ? 'deleted' : report.is_active === false ? 'deactivated' : 'active';

/**
 * Manage Reports state: every row from Postgres, what state each is in, and the three actions.
 *
 * Reloads from the server after each action rather than patching the row in place — activation and
 * deletion both touch fields the server owns (`updated_by`, `updated_at`, the composite status), and
 * a locally-guessed row would show a different audit trail than the one that was recorded.
 */
export const useManageReports = () => {
  const [reports, setReports] = useState<readonly PnlReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busyId, setBusyId] = useState<number>();
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const rows = await listAllPnlReports(signal);
      if (!signal?.aborted) {
        setReports(rows);
        setError(undefined);
      }
    } catch (caught) {
      if (!signal?.aborted) setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const act = useCallback(
    async (reportId: number, action: () => Promise<unknown>) => {
      setBusyId(reportId);
      try {
        await action();
        await load();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        setBusyId(undefined);
      }
    },
    [load]
  );

  const visible = useMemo(() => reports.filter((report) => showDeleted || managedReportState(report) !== 'deleted'), [reports, showDeleted]);
  const counts = useMemo(
    () => ({
      active: reports.filter((r) => managedReportState(r) === 'active').length,
      deactivated: reports.filter((r) => managedReportState(r) === 'deactivated').length,
      deleted: reports.filter((r) => managedReportState(r) === 'deleted').length
    }),
    [reports]
  );

  return {
    reports: visible,
    counts,
    loading,
    error,
    busyId,
    showDeleted,
    setShowDeleted,
    search,
    setSearch,
    refresh: () => void load(),
    setActive: (reportId: number, isActive: boolean) => act(reportId, () => setPnlReportActivation(reportId, isActive)),
    remove: (reportId: number) => act(reportId, () => deletePnlReport(reportId))
  };
};
